import { Component, OnInit, Input, OnDestroy } from "@angular/core";
import { Router, ActivatedRoute } from "@angular/router";
import { UntypedFormGroup, UntypedFormBuilder, Validators } from "@angular/forms";

import { AuthService } from "./../auth.service";
import { AppSettingsService } from "./../../shared/services/app-settings.service";
import { UserService } from "./../../shared/services/user.service";
import { ViewStateService } from "./../../shared/services/view-state.service";
import { AuthMocksService } from "./../../shared/services/mocks/auth-mocks.service";
import { CookieService } from "ngx-cookie-service";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

const CookieParams = {
  aarcid: "aarc_id",
  examid: "exam_id",
  path: "/",
  expirationDate: new Date(Date.now() + 2 * 60 * 60 * 1000)
};

@Component({
  selector: "app-sign-in",
  templateUrl: "./sign-in.component.html",
  styleUrls: ["./sign-in.component.css"],
})
export class SignInComponent implements OnInit, OnDestroy {
  @Input() $email: string;
  @Input() $myMessage: string;
  signInForm: UntypedFormGroup;
  loginError: string;
  aarcId: string;
  examId: string;
  showPassword: boolean;
  private cookieParams = CookieParams;
  isShowCCDown = false;
  private formValues = {
    email: "",
    password: "",
  };
  private destroy$ = new Subject<void>();

  constructor(
    private cookieService: CookieService,
    private formBuilder: UntypedFormBuilder,
    private appSettings: AppSettingsService,
    private authService: AuthService,
    private userService: UserService,
    private viewState: ViewStateService,
    private authMocksService: AuthMocksService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.isShowCCDown = (<any>window).isShowCCDown;
  }

  ngOnInit() {
    this.aarcId = this.getAARCId();
    this.examId = this.getExamId();
    this.showPassword = false;
    this.checkForUser();
    if (this.$email) {
      this.formValues.email = this.$email;
    }
    this.buildForm();
    if (this.$email) {
      this.signInForm.patchValue({ email: this.$email });
    }
  }

  onSubmit(updatedFormValues: any) {
    this.viewState.startLoading();

    this.formValues = updatedFormValues;
    this.validateForm();
    this.handleFormResult();
  }

  toggleShowPassword() {
    this.showPassword = !this.showPassword;
  }

  private handleFormResult() {
    this.loginError = "";
    if (this.signInForm.valid) {
      this.authService
        .signIn(
          this.formValues.email,
          this.formValues.password,
          this.aarcId,
          this.examId
        )
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          (success: boolean) => {
            this.viewState.stopLoading();
          },
          (e) => {
            const r = e;
            if (r && r.message == "JE") {
              this.loginError = this.validationMessages.je;
            } else if (r && r.message == "LOCKED") {
              this.loginError = this.validationMessages.locked;
            } else {
              this.loginError = this.validationMessages.login;
            }
            this.viewState.stopLoading();
          }
        );
    }
  }

  private checkForUser() {
    if (this.$email) {
      //do nothing?
    } else if (this.$myMessage) {
      //do nothing
    } else if (this.userService.CurrentUser && this.aarcId === "") {
      this.router.navigate([""]);
    } else {
      this.authService.signOut();
    }
  }

  private buildForm(): void {
    this.signInForm = this.formBuilder.group({
      email: [this.formValues.email, [Validators.required, Validators.email]],
      password: [this.formValues.password, Validators.required],
    });
  }

  private validateForm() {
    if (!this.signInForm) {
      return;
    }
    let hasErrors = false;

    for (const field in this.formErrors) {
      // clear previous error message (if any)
      this.formErrors[field] = "";
      const control = this.signInForm.get(field);
      if (control && !control.valid) {
        hasErrors = true;
        const messages = this.validationMessages[field];
        for (const key in control.errors) {
          this.formErrors[field] = messages[key];
          break;
        }
      }
    }

    if (hasErrors) {this.viewState.stopLoading();}
  }

  private getAARCId(): string {
    const root = this.route.snapshot.root;
    const aarcId = root.queryParams["aarcId"]
      ? root.queryParams["aarcId"]
      : this.cookieService.get(this.cookieParams.aarcid);

    if (aarcId) {
      this.cookieService.set(
        this.cookieParams.aarcid,
        aarcId,
        this.cookieParams.expirationDate,
        this.cookieParams.path
      );
    }
    return aarcId;
  }

  private getExamId(): string {
    const root = this.route.snapshot.root;
    const examId = root.queryParams["examId"]
      ? root.queryParams["examId"]
      : this.cookieService.get(this.cookieParams.examid);

    if (examId) {
      this.cookieService.set(
        this.cookieParams.examid,
        examId,
        this.cookieParams.expirationDate,
        this.cookieParams.path
      );
    }
    return examId;
  }

  formErrors = {
    email: "",
    password: "",
  };
  validationMessages = {
    email: {
      required: "Please enter a valid email.",
      email: "Please enter a valid email.",
    },
    password: {
      required: "Please enter a valid password.",
    },
    login: "Email and/or password is incorrect.",
    je: "Login Locked out. Contact NBRC",
    locked: "Too many bad attempts. Account login locked for 30 minutes.",
  };

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
